/**
 * O vocabulário de retenção, num só lugar para as telas que o usam (apps de
 * inclusão, exclusão e alteração, Conferir e Exportar movimentações).
 *
 * Dois estados, dois nomes, e nunca um pelo outro:
 *
 * - **Retido** (`SKIPED`): parado ANTES de aprovar, por decisão humana (falta
 *   documento, dado a confirmar com o RH). Ações: "Reter" e "Tirar retenção".
 *   Tirar a retenção devolve a "Pronto".
 * - **Criticado** (`RETAINED`): um dado precisa ser CORRIGIDO. A origem é o
 *   canal por onde a crítica chegou (decisão de 05/10/2026, D18 revista):
 *   "Criticado pela operadora" (crítica da QBem, ou o motivo
 *   "A operadora recusou (portal ou e-mail)" na conferência manual) ou
 *   "Criticado na conferência manual" (marcado por uma pessoa na tela). Estas
 *   telas não leem a origem, por isso o rótulo nomeia os dois canais.
 *   Desfaz-se com "Marcar como corrigido"; na exportação isso devolve a
 *   "Aprovado", nos apps a "Pronto". O backend usa o mesmo rótulo nas métricas.
 *
 * "Liberar" fica reservado para antecipar (Em espera para Pronto numa exclusão)
 * e não aparece aqui.
 *
 * Cada texto diz só o que o backend garante (revisão da PR #632):
 * - Na inclusão, sair da retenção grava "Em espera" (DELAYED) quando o início
 *   do plano é futuro (`app-inclusion-save/build-save-sql.ts`), por isso a
 *   inclusão tem contexto próprio.
 * - Na exportação, marcar como corrigido não garante o próximo arquivo: com
 *   QBem, quem já tem marca de envio nesta geração é tirado do arquivo
 *   (`dropAlreadyShipped`).
 * - Nada observa a correção do dado, e ninguém é avisado da devolução: a pessoa
 *   só volta quando alguém marca como corrigido.
 * - Salvar as marcações da exportação grava status na massa; não envia nada a
 *   ninguém.
 *
 * Histórico: até 25/09/2026 a exportação chamava "Reter/Liberar" o que gravava
 * e exibia como "Retificar", e os apps usavam as mesmas palavras para o outro
 * estado. Em 28/09/2026 "Retificar" deu lugar a "Criticado ou devolvido" e
 * "Liberar" a "Tirar retenção" / "Marcar como corrigido". Em 05/10/2026
 * "Criticado ou devolvido" deu lugar ao canal da crítica.
 */

/** SKIPED. */
export const ROTULO_RETIDO = "Retido";
/** RETAINED no chip, onde falta espaço. */
export const ROTULO_CRITICADO_CURTO = "Criticado";
/** RETAINED em filtros, tooltips e contadores. Igual ao das métricas do backend. */
export const ROTULO_CRITICADO = "Criticado pela operadora ou na conferência manual";

/**
 * Motivo da conferência manual que vira "Criticado pela operadora".
 * invariant: mesmo literal de MOTIVO_OPERADORA_RECUSOU no backend
 * (estado-pessoa/vocabulario.ts); o SQL do estado lê este texto.
 */
export const MOTIVO_OPERADORA_RECUSOU = "A operadora recusou (portal ou e-mail)";

/** Motivos de "Devolver para correção" na exportação (grava RETAINED). */
export const MOTIVOS_DA_CONFERENCIA = [
  "Documentação Pendente",
  "Dados Incorretos",
  "Aguardando Aprovação",
  MOTIVO_OPERADORA_RECUSOU,
] as const;

export const ACAO_RETER = "Reter beneficiário";
/** Desfaz um Retido (SKIPED). */
export const ACAO_TIRAR_RETENCAO = "Tirar retenção";
/** Desfaz um RETAINED. */
export const ACAO_MARCAR_CORRIGIDO = "Marcar como corrigido";
/** Na exportação, leva um aprovado a RETAINED. Também nomeia a etapa e a coluna. */
export const ACAO_DEVOLVER = "Devolver para correção";

/** Depois de aprovar no Conferir: leva ao Exportar do mesmo contrato. */
export const ACAO_IR_PARA_EXPORTACAO = "Concluir e ir para a exportação";
/** No lugar do botão acima quando o contrato não tem o Exportar. */
export const AVISO_CORRETORA_EXPORTA = "A corretora foi avisada e fará a exportação.";

/** Coluna do ícone de reter nos apps: reter, tirar retenção ou marcar como corrigido. */
export const COLUNA_RETENCAO = "Retenção";

/** INCLUSAO é a aprovação de inclusões, onde sair da retenção pode dar "Em espera". */
export type ContextoRetencao = "APROVACAO" | "INCLUSAO" | "EXPORTACAO";

export interface TextosRetencao {
  titulo: string;
  explicacao: string;
  botao: string;
  /** Pergunta de confirmação quando a pessoa já está retida, criticada ou devolvida. */
  aviso?: string;
}

export function estaParado(status: string | undefined): boolean {
  return status === "RETAINED" || status === "SKIPED";
}

export function textosDoModal(
  status: string | undefined,
  contexto: ContextoRetencao,
): TextosRetencao {
  if (status === "RETAINED") {
    const destino =
      contexto === "EXPORTACAO"
        ? 'volta para "Aprovado" e entra na próxima exportação, a menos que já tenha sido enviada à operadora nesta atualização'
        : destinoNaAprovacao(contexto);
    return {
      titulo: "Correção resolvida?",
      explicacao: `Esta pessoa está como "${ROTULO_CRITICADO}": a operadora recusou o registro ou ele foi criticado na conferência manual, e um dado precisa ser corrigido antes de seguir${
        contexto === "EXPORTACAO" ? ", por isso fica fora da exportação" : ""
      }. Ao marcar como corrigido, ela ${destino}.`,
      botao: ACAO_MARCAR_CORRIGIDO,
      aviso: `Os dados abaixo já foram corrigidos? Se sim, clique em "${ACAO_MARCAR_CORRIGIDO}".`,
    };
  }

  if (status === "SKIPED") {
    return {
      titulo: ACAO_TIRAR_RETENCAO,
      explicacao: `Esta pessoa está retida antes da aprovação. Ao tirar a retenção, ela ${destinoNaAprovacao(contexto)}.`,
      botao: ACAO_TIRAR_RETENCAO,
      aviso: `As pendências abaixo foram resolvidas? Se sim, clique em "${ACAO_TIRAR_RETENCAO}".`,
    };
  }

  if (contexto === "EXPORTACAO") {
    return {
      titulo: ACAO_DEVOLVER,
      explicacao:
        "Ao devolver para correção, a pessoa fica fora das próximas exportações até ser marcada como corrigida.",
      botao: ACAO_DEVOLVER,
    };
  }

  return {
    titulo: ACAO_RETER,
    explicacao:
      'Ao reter, a pessoa fica com o status "Retido" e não pode ser aprovada até que se resolva a pendência abaixo.',
    botao: ACAO_RETER,
  };
}

/** Nome da ação do ícone da linha: o mesmo do botão do modal que ele abre. */
export function acaoDaLinha(status: string | undefined, contexto: ContextoRetencao): string {
  return textosDoModal(status, contexto).botao;
}

function destinoNaAprovacao(contexto: ContextoRetencao): string {
  return contexto === "INCLUSAO"
    ? 'volta para "Pronto" e pode ser aprovada. Se o início do plano for futuro, fica "Em espera" até a data'
    : 'volta para "Pronto" e pode ser aprovada';
}

/**
 * Dica do chip de status. No Retido, o motivo da retenção, para não se confundir
 * com o "Motivo da alteração"; no RETAINED, o rótulo por extenso. Vazio quando
 * não há nada gravado (backend antigo não devolve o motivo).
 */
export function dicaDoStatus(
  status: string | undefined,
  motivo: string | null | undefined,
  observacao: string | null | undefined,
): string {
  const m = motivo?.trim();
  const o = observacao?.trim();
  if (status === "SKIPED") {
    if (!m) return "";
    return o ? `Motivo da retenção: ${m}. Observação: ${o}` : `Motivo da retenção: ${m}`;
  }
  if (status === "RETAINED") return m ? `${ROTULO_CRITICADO}. Motivo: ${m}` : ROTULO_CRITICADO;
  return "";
}

/** Aviso na exportação enquanto há marcações da etapa "Devolver para correção" por salvar. */
export function avisoMarcacoesNaoSalvas(quantidade: number): string {
  return `Há ${quantidade} marcação(ões) em "${ACAO_DEVOLVER}" que ainda não foram salvas. Volte lá e clique em "Salvar" antes de exportar, senão elas não entram no arquivo.`;
}

export const DICA_MARCACOES_NAO_SALVAS = `Há marcações em "${ACAO_DEVOLVER}" que ainda não foram salvas. Salve ou limpe antes de exportar.`;

/**
 * Sucesso do "Salvar" da etapa "Devolver para correção". O save só grava o
 * status na massa: não envia nada à operadora nem avisa o RH. Por isso diz o
 * que mudou, e não "enviadas".
 */
export function resumoDasMarcacoesSalvas(devolvidos: number, corrigidos: number): string {
  const partes: string[] = [];
  if (devolvidos > 0) {
    partes.push(
      devolvidos === 1
        ? "1 registro criticado."
        : `${devolvidos} registros criticados.`,
    );
  }
  if (corrigidos > 0) {
    partes.push(
      corrigidos === 1
        ? "1 registro marcado como corrigido."
        : `${corrigidos} registros marcados como corrigidos.`,
    );
  }
  return partes.length ? partes.join(" ") : "Marcações salvas.";
}

/**
 * As opções do select "Motivo", mais o motivo já gravado quando ele não está na
 * lista. A crítica da operadora grava "Dados Incorretos", que os apps não
 * oferecem; sem isto o MUI rejeita o valor e o campo aparece vazio logo abaixo
 * do aviso "Os dados abaixo já foram corrigidos?". O extra vem marcado como
 * `somenteLeitura` para não virar uma escolha nova de Retido.
 */
export function opcoesDeMotivo(
  opcoes: readonly string[],
  gravado: string | undefined,
): { valor: string; somenteLeitura: boolean }[] {
  const lista = opcoes.map((valor) => ({ valor, somenteLeitura: false }));
  if (gravado && !opcoes.includes(gravado)) lista.push({ valor: gravado, somenteLeitura: true });
  return lista;
}

/** M1/D13: aprovar o Conferir por tipo de movimentação. */
export const TITULO_APROVAR_POR_TIPO = "Aprovar só:";
export const ROTULO_TOTAL = "Total";
export const ROTULO_TIPO_INCLUSAO = "Inclusões";
export const ROTULO_TIPO_EXCLUSAO = "Exclusões";
export const ROTULO_TIPO_ALTERACAO = "Alterações";
export const ROTULO_TIPO_TRANSFERENCIA = "Transferências";

/** M2/D12/R4: reter várias pessoas de uma vez nos apps de inclusão, exclusão e alteração. */
export function acaoReterSelecionadas(quantidade: number): string {
  return `Reter selecionadas (${quantidade})`;
}

export function resumoRetidasEmLote(quantidade: number): string {
  return `${quantidade} pessoa(s) marcada(s) como Retido. Salve para gravar.`;
}

/** Título do modal de Retido em lote: a contagem e os nomes (até 10, depois "e mais N"). */
export function tituloReterEmLote(nomes: string[]): string {
  const mostrados = nomes.slice(0, 10).join(", ");
  const resto = nomes.length - 10;
  return `Reter ${nomes.length} pessoa(s): ${mostrados}${resto > 0 ? ` e mais ${resto}` : ""}`;
}

/** M3/D5: tratar as críticas da operadora na própria Exportar. */
export function acaoTratarCorrecoes(quantidade: number): string {
  return `Tratar correções (${quantidade})`;
}
export const AVISO_SEM_CRITICA_A_TRATAR = "Nenhuma crítica a tratar.";
export const AVISO_CORRIGIDO_VOLTOU = "Correção salva. A pessoa voltou para a lista de envio.";
export const AVISO_CORRIGIDO_SEM_LINHA = "Cadastro salvo, mas a movimentação criticada não foi atualizada. Use \"Marcar como corrigido\" depois de conferir.";
export function resumoCorrecaoMista(voltaram: number, semLinha: number): string {
  return `${voltaram} pessoa(s) voltaram para a lista de envio; ${semLinha} ficaram criticadas (cadastro salvo, movimentação não atualizada).`;
}

export const avisoServidorAprovouMais = (aprovadas: number, escolhidas: number) =>
  `O servidor aprovou ${aprovadas} movimentação(ões), mais do que as ${escolhidas} escolhidas. Confira em Exportar antes de enviar.`;

export const erroAoDevolverAFila = (motivo: string) =>
  `Cadastro salvo, mas a pessoa não voltou para a lista de envio: ${motivo}. Use "Marcar como corrigido".`;
export const MOTIVO_GENERICO_DEVOLVER_FILA = "erro ao devolver a pessoa";
export const AVISO_ERRO_CARREGAR_CADASTRO = "Não foi possível carregar o cadastro para correção. Tente de novo.";
